import type * as React from 'react';
import type { ReactNode } from 'react';
import { cn } from '../../styles/lib/utils';
import type { AvatarUser } from '../../utils';
import { AvatarData } from '../AvatarData';
import { Badge, type BadgeColor } from '../Badge';
import { Icon } from '../Icon';
import { Text } from '../Text';
import { Tooltip } from '../Tooltip';

/** Why a run has nothing to download, or `download` once its archive is ready. */
export type ExportHistoryStatus = 'pending' | 'download' | 'no-data' | 'failed' | 'delayed' | null;

export type ExportHistoryRow = {
  id: string;
  date: string;
  time: string;
  user: AvatarUser;
  layerCount: number;
  /** One per file format the run requested. */
  formats: string[];
  status: ExportHistoryStatus;
};

export type ExportHistoryTableProps = Omit<React.ComponentProps<'div'>, 'onSelect'> & {
  rows: ExportHistoryRow[];
  onDownload: (row: ExportHistoryRow) => void;
  /** Wraps the layer count, so a consumer can attach a card listing the layers. */
  renderLayerCount?: (row: ExportHistoryRow, count: ReactNode) => ReactNode;
  emptyTitle: string;
  emptyDescription: string;
  dateHeader?: string;
  userHeader?: string;
  statusHeader?: string;
  layersHeader?: string;
  formatsHeader?: string;
  completeLabel?: string;
  buildingLabel?: string;
  failedLabel?: string;
  noDataLabel?: string;
  /** A `delayed` row always reads as `pending` — there's no distinct visual identity for it. Passing
   *  this also folds `failed` into that same pill, distinguished only by the clock icon. */
  delayedTooltip?: string;
  delayedTooltipTestId?: (row: ExportHistoryRow) => string;
  /** Passing this also folds `no-data` into `download`'s pill, with this tooltip taking the action slot. */
  noDataTooltip?: string;
  noDataTooltipTestId?: (row: ExportHistoryRow) => string;
  /** Accessible name and hover text for the button that collects a finished run's archive. */
  downloadActionLabel?: string;
  downloadTestId?: (row: ExportHistoryRow) => string;
  layerCountTestId?: (row: ExportHistoryRow) => string;
  statusTestId?: (row: ExportHistoryRow) => string;
};

type StatusPresentation = { label: string; color: BadgeColor };

/** A run in an unrecognised state gets no pill rather than one claiming a status it doesn't have. */
function StatusCell({
  row,
  presentation,
  testId,
  delayedTooltip,
  delayedTooltipTestId,
}: {
  row: ExportHistoryRow;
  presentation: Record<NonNullable<ExportHistoryStatus>, StatusPresentation>;
  testId?: string;
  delayedTooltip?: string;
  delayedTooltipTestId?: string;
}) {
  if (!row.status) return null;

  const { label, color } = presentation[row.status];
  const showClock = delayedTooltip && (row.status === 'delayed' || row.status === 'failed');

  return (
    <Badge data-testid={testId} color={color} className="whitespace-nowrap">
      <span className="inline-flex items-center gap-1 leading-none">
        <span className="leading-none">{label}</span>
        {showClock && (
          <Tooltip
            text={delayedTooltip}
            aria-label={`About ${label} status`}
            triggerClassName="leading-none"
            data-testid={delayedTooltipTestId}
          >
            <Icon icon="clock" size={12} />
          </Tooltip>
        )}
      </span>
    </Badge>
  );
}

/** Only a finished run has something to collect; a no-data run explains itself instead. */
function ActionCell({
  row,
  onDownload,
  downloadActionLabel,
  testId,
  noDataTooltip,
  noDataTooltipTestId,
}: {
  row: ExportHistoryRow;
  onDownload: (row: ExportHistoryRow) => void;
  downloadActionLabel: string;
  testId?: string;
  noDataTooltip?: string;
  noDataTooltipTestId?: string;
}) {
  if (row.status === 'no-data' && noDataTooltip) {
    return (
      <Tooltip
        text={noDataTooltip}
        aria-label="Why there is no download"
        triggerClassName="border-grey-30 text-grey-60 h-7 w-7 justify-center rounded-md border"
        data-testid={noDataTooltipTestId}
      >
        <Icon icon="infoCircled" size={14} />
      </Tooltip>
    );
  }

  if (row.status !== 'download') return null;

  return (
    <button
      type="button"
      data-testid={testId}
      aria-label={downloadActionLabel}
      title={downloadActionLabel}
      onClick={() => onDownload(row)}
      className="border-grey-30 text-grey-80 hover:bg-grey-20 inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-md border focus-ring"
    >
      <Icon icon="download" size={14} />
    </button>
  );
}

function ExportHistoryTable({
  className,
  rows,
  onDownload,
  renderLayerCount,
  emptyTitle,
  emptyDescription,
  dateHeader = 'Date',
  userHeader = 'Exported By',
  statusHeader = 'Status',
  layersHeader = 'Layers',
  formatsHeader = 'Type(s)',
  completeLabel = 'Complete',
  buildingLabel = 'Building Export',
  failedLabel = 'Export Failed',
  noDataLabel = 'No Data Found',
  delayedTooltip,
  delayedTooltipTestId,
  noDataTooltip,
  noDataTooltipTestId,
  downloadActionLabel = 'Download this file',
  downloadTestId,
  layerCountTestId,
  statusTestId,
  ...props
}: ExportHistoryTableProps) {
  const pendingPresentation: StatusPresentation = { label: buildingLabel, color: 'blue' };

  // `delayed` has no visual identity of its own — it always reads as still-building, regardless of
  // any flag. The clock is gated on the viewer's own flag read, not the row's real escalation state —
  // accepted since the flag flips globally, not per-viewer, and escalation volume is low. `failed`
  // joins this same look only once that flag reads true.
  const presentation: Record<NonNullable<ExportHistoryStatus>, StatusPresentation> = {
    download: { label: completeLabel, color: 'green' },
    pending: pendingPresentation,
    delayed: pendingPresentation,
    failed: { label: failedLabel, color: 'red' },
    'no-data': { label: noDataLabel, color: 'grey' },
  };

  if (delayedTooltip) presentation.failed = pendingPresentation;
  if (noDataTooltip) presentation['no-data'] = presentation.download;

  const columns = [
    { header: dateHeader, width: 'w-22' },
    { header: userHeader, width: 'w-auto' },
    { header: statusHeader, width: 'w-30' },
    { header: layersHeader, width: 'w-14' },
    { header: formatsHeader, width: 'w-16' },
  ];

  return (
    <div
      data-slot="export-history-table"
      className={cn('border-grey-30 w-full overflow-hidden rounded-lg border', className)}
      {...props}
    >
      <table className="w-full table-fixed border-collapse text-left">
        <thead className="border-grey-30 border-b">
          <tr>
            {columns.map(({ header, width }) => (
              <th key={header} className={cn('px-2 py-2', width)}>
                <Text className="text-grey-90 text-xs font-medium">{header}</Text>
              </th>
            ))}
            <th className="w-11 px-2 py-2" />
          </tr>
        </thead>

        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={6} className="px-2 py-8">
                <div className="flex flex-col items-center gap-2 text-center">
                  <Text className="text-grey-60 text-xs">{emptyTitle}</Text>
                  <Text className="text-grey-60 text-xs">{emptyDescription}</Text>
                </div>
              </td>
            </tr>
          ) : (
            rows.map((row, index) => {
              const count = (
                <Text data-testid={layerCountTestId?.(row)} className="text-blue-50 text-xs">
                  {row.layerCount}
                </Text>
              );

              return (
                <tr
                  key={row.id}
                  data-status={row.status ?? 'unknown'}
                  className={cn(
                    // A run in progress is what the user is waiting on, so it is marked out rather
                    // than left to the alternating stripe.
                    row.status === 'pending' ? 'bg-blue-10' : index % 2 === 1 && 'bg-grey-10',
                  )}
                >
                  <td className="px-2 py-2 align-middle">
                    <div className="flex flex-col">
                      <Text className="text-grey-90 text-xs">{row.date}</Text>
                      <Text className="text-grey-60 text-2xs">{row.time}</Text>
                    </div>
                  </td>
                  <td className="px-2 py-2 align-middle">
                    <Tooltip text={row.user.name} triggerClassName="flex w-full min-w-0">
                      <AvatarData user={row.user} size="small" />
                    </Tooltip>
                  </td>
                  <td className="px-2 py-2 align-middle">
                    <StatusCell
                      row={row}
                      presentation={presentation}
                      testId={statusTestId?.(row)}
                      delayedTooltip={delayedTooltip}
                      delayedTooltipTestId={delayedTooltipTestId?.(row)}
                    />
                  </td>
                  <td className="px-2 py-2 align-middle">{renderLayerCount ? renderLayerCount(row, count) : count}</td>
                  <td className="px-2 py-2 align-middle">
                    <div className="flex flex-wrap gap-1">
                      {row.formats.map((format) => (
                        <Badge key={format} color="grey" className="text-2xs">
                          {format}
                        </Badge>
                      ))}
                    </div>
                  </td>
                  <td className="px-2 py-2 text-right align-middle">
                    <ActionCell
                      row={row}
                      onDownload={onDownload}
                      downloadActionLabel={downloadActionLabel}
                      testId={downloadTestId?.(row)}
                      noDataTooltip={noDataTooltip}
                      noDataTooltipTestId={noDataTooltipTestId?.(row)}
                    />
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

export { ExportHistoryTable };
